<template>
  <section>
    <div class="my-spacebetween">
      <div class="mx-auto max-w-content px-sidespacing">
        <h2
          class="animate-fade mb-8 text-6xl font-extrabold uppercase m:text-5xl"
        >
          {{ props.newsData?.heading }}
        </h2>
        <div
          class="group/list relative flex w-full grow flex-col"
          ref="news-list"
          @mouseleave="handleListLeave"
        >
          <ul class="flex w-full flex-col">
            <li
              v-for="(item, index) in props.newsData?.news"
              :key="`news-item-${index}`"
              class="group/item animate-fade border-t-[3px] p-6 transition-colors duration-300 hover:!border-opacity-100 group-hover/list:border-opacity-50 m:px-0"
              @mouseenter="handleMouseEnter(index)"
              @mouseleave="handleMouseLeave"
            >
              <a
                :href="item.url"
                class="flex w-full items-center justify-between"
              >
                <div
                  class="flex flex-col gap-8 transition-opacity duration-300 m:gap-6 sm:group-hover/item:!opacity-100 sm:group-hover/list:opacity-50"
                >
                  <div class="bg-primary w-fit rounded-md px-3 py-2 text-white">
                    <span class="text-sm font-bold uppercase">
                      {{ item.datum }}
                    </span>
                  </div>
                  <p
                    class="text-5xl font-light uppercase m:text-xl m:font-extrabold"
                  >
                    {{ item.title }}
                  </p>
                </div>
              </a>
            </li>
          </ul>
          <template
            v-for="(item, index) in props.newsData?.news"
            :key="`news-img-${index}`"
          >
            <img
              v-if="item.image?.src && isHoverSupported"
              :src="item.image.src"
              :alt="item.image.alt || ''"
              loading="lazy"
              class="pointer-events-none absolute right-10 z-10 size-full max-w-12/24 rounded-xl object-cover transition-opacity duration-500 m:hidden"
              :class="{
                'opacity-100': activeIndex === index,
                'opacity-0': activeIndex !== index,
              }"
              :style="{
                top: `${imageTop}px`,
                transform: `translate(${imageOffset}px, -50%)`,
                // maxHeight: `50vh`,
                // minHeight: `500px`,
                                objectPosition: item.image.focalPosition,
              }" style="height: clamp(500px, 50vh, 800px)"
            />
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useTemplateRef, ref, watch, computed, onMounted } from 'vue';
import {
  TransitionPresets,
  useMouse,
  useElementBounding,
  useThrottleFn,
  useTransition,
  useEventListener,
  useMediaQuery,
} from '@vueuse/core';

interface ImageProps {
  src: string;
  focalPosition: string;
  alt?: string;
}

interface NewsItem {
  title: string;
  datum: string;
  url: string;
  image?: ImageProps;
}

interface NewsData {
  heading: string;
  news: NewsItem[];
}

const props = defineProps<{
  newsData: NewsData;
}>();

const isHoverSupported = useMediaQuery('(hover: hover)');

const newsList = useTemplateRef<HTMLElement>('news-list');
const { y: mouseY, x: mouseX } = useMouse({ type: 'client' });
const {
  top: listTop,
  left: listLeft,
  width: listWidth,
} = useElementBounding(newsList);

const throttledMouseY = ref(0);
const throttledMouseX = ref(0);

watch(
  [mouseY, mouseX],
  useThrottleFn(([newY, newX]) => {
    throttledMouseY.value = newY;
    throttledMouseX.value = newX;
  }, 30),
);

const transitionConfig = {
  duration: 300,
  transition: TransitionPresets.easeOutCubic,
};

const smoothMouseX = useTransition(throttledMouseX, transitionConfig);
const smoothMouseY = useTransition(throttledMouseY, transitionConfig);

const imageTop = computed(() => smoothMouseY.value - listTop.value);
const imageOffset = computed(() => {
  const relativeX = smoothMouseX.value - listLeft.value;
  const centerOffset = relativeX - listWidth.value / 2;
  return centerOffset * 0.2;
});
const activeIndex = ref<number | null>(null);
let hoverTimeout: ReturnType<typeof setTimeout>;

const handleMouseEnter = (index: number) => {
  // Snap the smoothed values to the current mouse position so the transition
  // always starts from the correct coordinates instead of a stale position.
  throttledMouseY.value = mouseY.value;
  throttledMouseX.value = mouseX.value;
  hoverTimeout = setTimeout(() => {
    activeIndex.value = index;
  }, 200);
};

const handleMouseLeave = () => {
  clearTimeout(hoverTimeout);
};

const handleListLeave = () => {
  clearTimeout(hoverTimeout);
  activeIndex.value = null;
};

useEventListener(
  window,
  'scroll',
  () => {
    const container = newsList.value;
    if (!container) {
      handleListLeave();
      return;
    }

    const el = document.elementFromPoint(mouseX.value, mouseY.value);

    if (!el || !container.contains(el)) {
      handleListLeave();
      return;
    }

    // Cursor is still inside the container — find which <li> it's over.
    const li = el.closest('li');
    if (!li) {
      handleListLeave();
      return;
    }

    const index = Array.from(container.querySelectorAll('li')).indexOf(li);
    if (index === -1) {
      handleListLeave();
      return;
    }

    // Snap position so the image appears at the right spot immediately.
    clearTimeout(hoverTimeout);
    throttledMouseY.value = mouseY.value;
    throttledMouseX.value = mouseX.value;
    activeIndex.value = index;
  },
  { passive: true },
);
</script>
